import { useMemo } from 'react';
import { ProgramID } from '@constants';
import { UserRoleEnum } from '../types';
import { isDemoAccountUser } from './useDemoAccount';
import { useUser } from './useUser';

// The Lesson Library tile, side menu item and Programs tab only show when the school has the
// "LessonLibrary" program selected (Super Admin → Edit School → Program). Each region has its own
// LessonLibrary program: AU uses AUSLESSONLIBRARY, US and CA share LESSONLIBRARY. The API applies the
// same rule (getProgram returns null for a program the school doesn't have), so without it the page
// would only ever show an empty library.

type LessonLibraryCheckUser = {
  id?: string | null;
  role?: string | null;
  customer?: { isDemo?: boolean | null } | null;
  schools?: Array<{ programs?: string | null } | null> | null;
} | null | undefined;

const lessonLibraryProgramId = (userId: string): string | undefined => {
  if (userId.startsWith('AU_')) return ProgramID.AUSLESSONLIBRARY;
  if (userId.startsWith('US_') || userId.startsWith('CA_')) return ProgramID.LESSONLIBRARY;
  return undefined;
};

export const hasLessonLibrary = (user: LessonLibraryCheckUser): boolean => {
  if (isDemoAccountUser(user)) return false; // OP-3222: demo accounts only see the Programs tab
  // Mirrors the API: getProgram returns every program to a Super Admin, whatever their school has.
  if (user?.role === UserRoleEnum.SuperAdmin) return true;
  const programId = lessonLibraryProgramId(user?.id ?? '');
  if (!programId) return true;
  return !!user?.schools?.[0]?.programs?.includes(programId);
};

export const useHasLessonLibrary = (): boolean => {
  const [user] = useUser();
  return useMemo(
    () => hasLessonLibrary(user),
    [user?.id, user?.role, user?.customer?.isDemo, user?.schools?.[0]?.programs]
  );
};
